import { Avatar, Button, IconButton, Tooltip } from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import { MetaConversation, Presence } from 'lib-shared-legacy-types';
import { User } from 'lib-shared-types-api';
import noop from 'lodash/noop';
import { Component } from 'react';

import { buttonize } from 'components/shared/util/buttonize';

import styles from './userItem.module.scss';

type PropTypes = {
  user: User;
  presence: Presence;

  metaConversation: MetaConversation;
  numberTransferRequests: Array<any>;
  transferToAgentRequests: Array<any>;
  disabled: boolean;
  asyncStatus: string;
  highlighted: boolean;
  isAgent?: boolean;
  className?: string;

  onAssistedTransferClick: (payload: { requestToUser: User }) => void;
  onBlindTransferClick: (payload: { requestToUser: User }) => void;
};

type StateT = {
  isOpen: boolean;
};

export default class UserItem extends Component<PropTypes, StateT> {
  constructor(props: PropTypes) {
    super(props);
    this.state = {
      isOpen: false,
    };

    this.handleBlindTransfer = this.handleBlindTransfer.bind(this);
    this.handleAssistedTransfer = this.handleAssistedTransfer.bind(this);
  }

  toggle = () => {
    this.setState({ isOpen: !this.state.isOpen });
  };

  handleBlindTransfer() {
    const { onBlindTransferClick, user } = this.props;
    onBlindTransferClick({ requestToUser: user });
  }

  handleAssistedTransfer() {
    const { onAssistedTransferClick, user } = this.props;
    onAssistedTransferClick({ requestToUser: user });
  }

  isPhoneChannelTurnedOn() {
    const { presence, metaConversation } = this.props;
    for (const workingMedia of presence.working_channels) {
      if (
        workingMedia.conversation_channel === 'pstnphone' &&
        workingMedia.working === false &&
        metaConversation.conversation.initial_channel === 'pstnphone'
      ) {
        return true;
      }
    }

    return false;
  }

  isAgentOnThePhone() {
    const { presence, metaConversation } = this.props;
    if (
      metaConversation.conversation.initial_channel === 'pstnphone' ||
      metaConversation.conversation.initial_channel === 'callback'
    ) {
      for (const load of presence.presence_status.load_descriptions) {
        // We can not transfer a phone/callback if the agent has a phone/callback conversation.
        if (
          load.load_type !== 'nonlive' &&
          (load.conversation_channel === 'pstnphone' ||
            load.conversation_channel === 'callback')
        ) {
          return true;
        }
      }
    }

    return false;
  }

  render() {
    const {
      className,
      asyncStatus,
      disabled,
      highlighted,
      metaConversation,
      numberTransferRequests,
      presence,
      transferToAgentRequests,
      user,
    } = this.props;

    const { isOpen } = this.state;

    let name: string | null | undefined = '';
    let realNameOrThisAgent: string | null | undefined = 'This agent';
    let disableAllTransfers = disabled;
    let reassignDescription = `Transfer conversation #${metaConversation.conversation.id}.`;

    if (typeof user.name === 'string') {
      name = user.name;
      realNameOrThisAgent = name;
    } else if (typeof user.phone_number === 'string') {
      name = user.phone_number;
    } else if (typeof user.email === 'string') {
      name = user.email;
    }

    if (!disabled) {
      const requestToAgent = transferToAgentRequests.find(
        (request: any) =>
          request.metaConversation.conversation.id ===
          metaConversation.conversation.id,
      );

      const requestToNumber = numberTransferRequests.find(
        (request: any) =>
          request.metaConversation.conversation.id ===
          metaConversation.conversation.id,
      );

      if (metaConversation.requester?.id === user.id) {
        disableAllTransfers = true;
        reassignDescription = `${realNameOrThisAgent} is already in the conversation.`;
      } else if (presence) {
        if (this.isPhoneChannelTurnedOn()) {
          disableAllTransfers = true;
          reassignDescription = `${realNameOrThisAgent} has the phone channel turned off.`;
        }

        if (this.isAgentOnThePhone()) {
          // We can not transfer a phone/callback if the agent has a phone/callback conversation.
          disableAllTransfers = true;
          reassignDescription = `${realNameOrThisAgent} is on the phone.`;
        }
      }

      if (requestToAgent !== undefined || requestToNumber !== undefined) {
        disableAllTransfers = true;
        reassignDescription = 'Request already sent waiting for response.';
      }
    } else if (asyncStatus === 'loading') {
      reassignDescription = 'Loading';
      disableAllTransfers = true;
    }

    const initialChannel =
      this.props.metaConversation.conversation.initial_channel;
    const disableAssistedtransfer =
      disableAllTransfers ||
      [
        'widgetchat',
        'messenger',
        'contactform',
        'voicemail',
        'email',
        'facebookmessenger',
      ].includes(initialChannel as any);

    const rootClass = classnames(
      {
        [styles.root]: true,
        [styles.rootHighlighted]: highlighted,
        [styles.rootWorking]: presence.status === 'working',
        [styles.rootOffline]: presence.status === 'offline',
        [styles.rootAway]: presence.status === 'away',
        [styles.rootDim]: disableAllTransfers,
      },
      className,
    );

    return (
      <div
        className={rootClass}
        {...buttonize(this.toggle)}
        onClick={this.toggle}
      >
        <div className={styles.header}>
          <div className={styles.presenceDot} />
          <div className={styles.avatar}>
            {(user as any).icon !== undefined ? (
              <Avatar icon={(user as any).icon} />
            ) : (
              <Avatar user={user} />
            )}
          </div>
          <span className={styles.name}>{name}</span>
          <IconButton
            icon={this.state.isOpen ? 'arrow-down' : 'arrow-right'}
            variant="tertiary"
            onClick={this.toggle}
          />
        </div>
        {isOpen && (
          <div className={styles.buttons}>
            <Tooltip content={reassignDescription}>
              <Button
                variant="secondary"
                disabled={disableAllTransfers}
                type="button"
                onClick={disableAllTransfers ? noop : this.handleBlindTransfer}
              >
                Transfer now
              </Button>
            </Tooltip>
            <Tooltip content={reassignDescription}>
              <Button
                variant="secondary"
                disabled={disableAssistedtransfer}
                type="button"
                onClick={
                  disableAssistedtransfer ? noop : this.handleAssistedTransfer
                }
              >
                Talk to {name} first
              </Button>
            </Tooltip>
          </div>
        )}
      </div>
    );
  }
}
